(ns practitest.frontend.forms3.instance.events
  (:require [ajax.core :as ajax]
            [clojure.string :as str]
            [practitest.frontend.common-entities.events :as common-events]
            [practitest.frontend.common-entities.metadata :as metadata]
            [practitest.frontend.common.i18n :refer [get-translation]]
            [practitest.frontend.common.mixpanel :as mixpanel]
            [practitest.frontend.common.re-frame :refer [conditional-dispatch partial-dispatch
                                                          partial-dispatch-sync partial-subscribe]]
            [practitest.frontend.components.notifications :as notifications]
            [practitest.frontend.events :as events]
            [practitest.frontend.forms3.entity-form :as f3.entity-form]
            [practitest.frontend.forms3.entity-form.forms :as f3.forms]
            [practitest.frontend.forms3.instance.step-parameters :as step-parameters]
            [practitest.frontend.forms3.instance.version-selector :as instance-version-section]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.subs :as subs]
            [re-frame.core :as re-frame]))

(def ^:private entity-type :instance)
(def metadata (get metadata/entities-metadata entity-type))
(def ^:private form-id (get metadata :form-id))

(defn load-instance-event
  [db]
  (let [fetch-options {:form-id form-id
                       :entity-type entity-type
                       :entity-id (subs/get-current-entity-id db :instance)
                       :project-id (subs/get-current-project-id db)
                       :got-entity-data [::got-instance]}]
    [::f3.entity-form/fetch-entity fetch-options]))

(defn load-testset-event
  [db testset-id]
  (let [fetch-options {:form-id :testset
                       :entity-type :testset
                       :entity-id testset-id
                       :project-id (subs/get-current-project-id db)
                       :new? false}]
    [::f3.entity-form/fetch-entity fetch-options]))

;; Run-level fields (customizable_type "QtestRun") are ordinarily only ever
;; shown on the Run form itself. But an Exploratory Test has no Run row until
;; the session is finished, so their values live on the Instance record
;; instead (see Instance#run_level_fields_hash on the backend) - this form
;; lets the user fill them in before opening the ET editor.
(def run-fields-form-id :run-fields-instance)

(defn run-level-entity-fields
  [all-fields entity-fields]
  (->> (:run entity-fields)
       (filter (fn [[field-id _]] (:custom? (get all-fields field-id))))
       (into {})))

(re-frame/reg-sub
  ::run-level-fields
  (fn [_]
    [(re-frame/subscribe [::subs/fields])
     (re-frame/subscribe [::subs/all-entity-fields])])
  (fn [[all-fields entity-fields] _]
    (run-level-entity-fields all-fields entity-fields)))

(defn run-fields-reset-form-state
  [db instance]
  (let [run-level-fields (run-level-entity-fields (:fields db) (:entity-fields db))]
    (reduce
      (fn [db field-id]
        (assoc-in db [:form-states2 run-fields-form-id :fields field-id]
                  {:value (get instance field-id) :dirty? false}))
      (assoc-in db [:form-states2 run-fields-form-id] {})
      (keys run-level-fields))))

(def run-fields-form-props
  {:get-field-props (partial-subscribe [::f3.forms/field-props :run])
   :get-form-state  (partial-subscribe [::f3.forms/form-state run-fields-form-id])
   :set-form-state  (partial-dispatch [::f3.forms/set-form-state run-fields-form-id])

   :get-field-state (partial-subscribe [::f3.forms/field-state run-fields-form-id])
   :set-field-state (partial-dispatch-sync [::f3.forms/set-field-state run-fields-form-id])

   :form-ref (atom nil)})

(re-frame/reg-event-fx
  ::got-instance
  (fn [{db :db} [_ {:keys [save-and-run? testset-in-url?]} response]]
    (let [instance (:entity response)]
      (conditional-dispatch
        {:db (-> db
                 (step-parameters/reset-form-state instance)
                 (instance-version-section/reset-form-state instance)
                 (run-fields-reset-form-state instance))}
        ;; It's possible to arrive here via two different URLs (because of legacy UI) - one will have
        ;; testset-id and we will fetch testset information in edit controller and another way is with
        ;; instance id only (e.g. from run page) - need to do chained fetch call in that case
        (when (and (not testset-in-url?)
                   (not save-and-run?))
          (load-testset-event db (:suite_id instance)))))))

;; Kept outside :form-states2, which force-form-validation! reads and
;; rewrites wholesale - storing this flag there raced with that read-modify-write
;; and got silently clobbered back to true right after being set to false.
(def ^:private mandatory-modal-key ::mandatory-run-modal)

(re-frame/reg-event-db
  ::set-mandatory-modal-visible
  (fn [db [_ visible?]]
    (assoc-in db [mandatory-modal-key :show?] visible?)))

(re-frame/reg-sub
  ::mandatory-modal-visible?
  (fn [db _]
    (get-in db [mandatory-modal-key :show?] false)))

(re-frame/reg-event-fx
  ::run-test-success
  (fn [{db :db} [_ response]]
    (cond
      (and (:show_mandatory_modal response)
           @(re-frame/subscribe [::subs/new-run-form-enabled?]))
      {:dispatch [::set-mandatory-modal-visible true]}

      (seq (:errors response))
      {:dispatch [::notifications/error2
                  (str/join ";" (:errors response))
                  {:raw-html? true
                   :auto-close false}]}

      (some? (:redirect response))
      {::events/redirect-delayed (:redirect response)}

      :else
      {})))

(re-frame/reg-event-fx
  ::run-test-failure
  (fn [_ [_ error]]
    {:dispatch [::notifications/error2 (get-translation "instance.notifications.run-test-error"
                                                        "Error running test")]}))

(re-frame/reg-event-fx
  ::run-test
  (fn [{db :db} [_ instance options]]
    (mixpanel/track "Form Actions" {"Action" "Run Instance"})
    (let [url (broutes/get-url "instances#run_now" {:p_id (subs/get-current-project-id db)
                                                    :set_id (:suite_id instance)
                                                    :id (:id instance)})]
      {:http-xhrio {:method :post
                    :uri url
                    :format (ajax/json-request-format)
                    ;; true must be string here because of backend logic
                    :params (merge {:run_from_instance "true"}
                                   options)
                    :response-format (ajax/json-response-format {:keywords? true})
                    :on-success [::run-test-success]
                    :on-failure [::run-test-failure]}})))

(re-frame/reg-event-fx
  ::delete-instance-success
  (fn [{db :db} [_ response]]
    (cond
      (seq (:errors response))
      {:dispatch [::notifications/error2
                  (str/join ";" (:errors response))
                  {:raw-html? true
                   :auto-close false}]}

      (some? (:redirect response))
      {::events/redirect-delayed (:redirect response)}

      :else
      (js/history.back))))

(re-frame/reg-event-fx
  ::delete-instance-failure
  (fn [_ [_ error]]
    {:dispatch [::notifications/error2 (get-translation "instance.notifications.delete-instance-error"
                                                        "Error deleting instance")]}))

(re-frame/reg-event-fx
  ::delete-instance
  (fn [{db :db} [_ instance-id]]
    (let [url (broutes/get-url "instances#destroy" {:p_id (subs/get-current-project-id db)
                                                    :id instance-id})]
      {:http-xhrio {:method :delete
                    :uri url
                    :format (ajax/json-request-format)
                    :response-format (ajax/json-response-format {:keywords? true})
                    :on-success [::delete-instance-success]
                    :on-failure [::delete-instance-failure]}})))

(re-frame/reg-event-db
  ::reset-instance-form
  (fn [db [_ new?]]
    (-> db
        (f3.forms/reset-form-state form-id new? entity-type)
        (step-parameters/reset-form-state (get-in db [:entity entity-type :current]))
        (instance-version-section/reset-form-state (get-in db [:entity entity-type :current]))
        (run-fields-reset-form-state (get-in db [:entity entity-type :current])))))

(defn- instance-form-params-helper
  [save-and-run? db form-params]
  (let [step-params (filter (fn [[_k v]]
                              (or (:dirty? v)
                                  ;; Collect and send default parameters to server as well
                                  (:default? v)))
                            (get-in db [:form-states2 step-parameters/form-id :fields]))
        version-label-state (get-in db [:form-states2 form-id :fields :version_label])
        ;; Run-level fields are stored on the Instance record itself,
        ;; but persisted the same generic way as ordinary Instance custom fields - merged into :custom
        run-field-values (into {}
                                (comp (filter (fn [[_ v]] (:dirty? v)))
                                      (map (fn [[field-id v]]
                                             (if (= :user (:type (get (:fields db) field-id)))
                                               [field-id (str (or (:id (:value v)) (:value v)))]
                                               [field-id (:value v)]))))
                                (get-in db [:form-states2 run-fields-form-id :fields]))
        result (cond-> form-params

                       save-and-run?
                       ;; Backend expects 'true' as string
                       (assoc :save_and_run_instance "true")

                       (:dirty? version-label-state)
                       (assoc :label_id (:id (first (:value version-label-state))))

                       (seq step-params)
                       (assoc :step_param_names (into {}
                                                      (map (fn [[field-name field-state]]
                                                             [field-name (:value field-state)]))
                                                      step-params))

                       (seq run-field-values)
                       (update :custom merge run-field-values))]
    result))

(re-frame/reg-event-fx
  ::save-instance
  (fn [{db :db} [_ instance-id testset-id {:keys [new? save-and-run?] :as options}]]
    {:dispatch [::common-events/save-entity form-id entity-type
                (assoc options :route-params {:set_id testset-id}
                               :additional-params-helper (partial instance-form-params-helper save-and-run?)
                               :redirect-key :redirect
                               :got-entity-data [::got-instance])]}))

(re-frame/reg-event-fx
  ::delete-run-success
  (fn [{db :db} [_ response]]
    (let [fetch-options {:form-id form-id
                         :entity-type entity-type
                         :entity-id (subs/get-current-entity-id db :instance)
                         :project-id (subs/get-current-project-id db)
                         :got-entity-data [::got-instance]}]
      {:dispatch (load-instance-event db)})))

(re-frame/reg-event-fx
  ::delete-run-failure
  (fn [_ [_ error]]
    {:dispatch [::notifications/error2 (get-translation "instance.notifications.delete-run-error"
                                                        "Error deleting run")]}))

(re-frame/reg-event-fx
  ::delete-run
  (fn [{db :db} [_ run-id]]
    (let [url (broutes/get-url "qtest_runs#destroy" {:p_id (subs/get-current-project-id db)
                                                    :id run-id})]
      {:http-xhrio {:method :delete
                    :uri url
                    :format (ajax/json-request-format)
                    :response-format (ajax/json-response-format {:keywords? true})
                    :on-success [::delete-run-success]
                    :on-failure [::delete-run-failure]}})))

(re-frame/reg-event-fx
  ::rewind-run-success
  (fn [{db :db} [_ on-success _]]
    (conditional-dispatch {:dispatch-n [(load-instance-event db)
                                        [::notifications/success2 (get-translation "instance.notifications.rewind-run-success"
                                                                                   "Rewind run completed")]]}
                          on-success)))

(re-frame/reg-event-fx
  ::rewind-run
  (fn [{db :db} [_ run-id date on-success]]
    {:http-xhrio {:method          :post
                  :uri             (broutes/get-url "qtest_runs#rewind_runs_dates"
                                                    {:p_id (subs/get-current-project-id db)
                                                     :id run-id})
                  :params          {:date date}
                  :format          (ajax/json-request-format)
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [::rewind-run-success on-success]
                  :on-failure      [::events/api-error ::rewind-run
                                    {:message (get-translation "instance.notifications.rewind-run-error"
                                                               "Error rewinding run")}]}}))
(re-frame/reg-event-db
  ::toggle-expand-row
  (fn [db [_ row]]
    (let [expanded? @(re-frame/subscribe [:practitest.frontend.forms3.instance/expanded-row? row])]
      (update-in db
                 [:form-states2 form-id :expanded-runs]
                 (if expanded?
                   disj
                   (fnil conj #{}))
                 (:id row)))))